import type {
  RawAppointment,
  FaseServicio,
  Horario,
  CalendarEvent,
  DiaAusencia,
  RawDescanso,
} from '@/components/types/typeCalendar';

/**
 * 1. Mapeo de RawAppointment a CalendarEvent,
 *    incluyendo soporte para citas “desbordadas” multidiarias
 *    con fases distinguidas en cada segmento.
 */
export function mapearCitas(
  data: RawAppointment[],
  horarios: Horario[]
): CalendarEvent[] {
  const eventos: CalendarEvent[] = [];

  data.forEach((cita) => {
    const {
      id_cita,
      id_servicio,
      nombre_empresa,
      nombre_servicio,
      estado_cita,
      fecha_cita,
      hora_cita,
      fecha_fin,
      hora_fin,
      duracion_minutos,
      fases_servicio,
      desbordamiento,
      tipo_pago,
      observaciones,
      telefono,
      email,
      email_contacto,
      motivo_cancelacion,
      motivo_anulado,
      nombre_completo,
      precio,
      num_clientes,
      cliente_sugerido,
      reprogramada_cliente,
      historial_reprogramaciones,
    } = cita;

    // ⚠️ IMPORTANTE: NO meter trabajadores_en_cita aquí
    const commonProps = {
      estado: estado_cita,
      tipo_pago,
      observaciones,
      telefono,
      email,
      email_contacto,
      motivo_cancelacion: motivo_cancelacion || '',
      motivo_anulado: motivo_anulado || '',
      fecha_cancelacion: cita.fecha_cancelacion || null,
      fecha_anulado: cita.fecha_anulado || null,
      fecha_completado: cita.fecha_completado || null,
      duracion_minutos,
      nombre_completo,
      nombre_cliente: cita.nombre_cliente,
      precio,
      num_clientes,
      cliente_sugerido,
      tiene_valoracion: cita.tiene_valoracion,
      puntuacion_cita: cita.puntuacion_cita,
      comentario_valoracion: cita.comentario_valoracion ?? null,
      reprogramada_cliente: reprogramada_cliente ?? false,
      historial_reprogramaciones: historial_reprogramaciones ?? null,
    };

    // Base start datetime
    const startDate = new Date(fecha_cita);
    const [hStart, mStart] = hora_cita.split(':').map(Number);
    startDate.setHours(hStart, mStart, 0, 0);

    // Helper para crear eventos
    function pushEvent(
      start: Date,
      end: Date,
      fase: FaseServicio | null,
      faseTipo: 'atencion' | 'descanso'
    ) {
      eventos.push({
        id: fase
          ? `${id_cita}-${faseTipo}-${start.toISOString()}`
          : `${id_cita}-${start.toISOString()}`,
        groupId: id_cita,
        title: fase
          ? faseTipo === 'atencion'
            ? `${nombre_servicio} - ${fase.nombre_fase}`
            : fase.nombre_fase
          : nombre_servicio,
        start: start.toISOString(),
        end: end.toISOString(),
        classNames: [`fase-${faseTipo}`],
        display: faseTipo === 'descanso' ? 'background' : 'auto',
        backgroundColor: colorPorId(id_cita),
        borderColor: colorPorId(id_cita),
        extendedProps: {
          ...commonProps,
          tipo_fase: faseTipo,
          id_cita,
          id_servicio,
          nombre_empresa,
          raw_fecha_inicio: fecha_cita,
          raw_fecha_fin: fecha_fin,
          raw_hora_inicio: hora_cita,
          raw_hora_fin: hora_fin,
          fecha_cancelacion: cita.fecha_cancelacion,
          fecha_anulado: cita.fecha_anulado,

          // 🔥 AÑADIDO AQUÍ (NO EN COMMONPROPS)
          trabajadores_en_cita: cita.trabajadores_en_cita ?? null,
        },
      });
    }

    // -------------------------------
    // 2️⃣ Cita multidiaria (desbordada)
    // -------------------------------
    if (desbordamiento && fecha_fin && hora_fin) {
      const endDate = new Date(fecha_fin);
      const [hEnd, mEnd] = hora_fin.split(':').map(Number);
      endDate.setHours(hEnd, mEnd, 0, 0);

      const franjasPorDia = obtenerHorariosPorDia(horarios);

      const fases = Array.isArray(fases_servicio) ? fases_servicio : [];
      let faseIdx = 0;
      let faseRemaining = fases[0]?.duracion_minutos ?? 0;

      let dayCursor = new Date(startDate);
      dayCursor.setHours(0, 0, 0, 0);

      while (dayCursor.getTime() <= endDate.getTime()) {
        const isFirstDay =
          dayCursor.toDateString() === startDate.toDateString();
        const isLastDay = dayCursor.toDateString() === endDate.toDateString();

        const dayStartLimit = isFirstDay ? startDate : new Date(dayCursor);
        const dayEndLimit = isLastDay
          ? endDate
          : new Date(dayCursor.getTime() + 24 * 3600 * 1000);

        const franjas = franjasPorDia[dayCursor.getDay()] || [];

        franjas.forEach(({ hora_inicio, hora_fin }) => {
          const franjaStart = new Date(dayCursor);
          const [fh, fm] = hora_inicio.split(':').map(Number);
          franjaStart.setHours(fh, fm, 0, 0);

          const franjaEnd = new Date(dayCursor);
          const [th, tm] = hora_fin.split(':').map(Number);
          franjaEnd.setHours(th, tm, 0, 0);

          const segmentStart =
            franjaStart > dayStartLimit ? franjaStart : dayStartLimit;
          const segmentEnd = franjaEnd < dayEndLimit ? franjaEnd : dayEndLimit;

          if (segmentStart >= segmentEnd) return;

          if (fases.length === 0) {
            pushEvent(segmentStart, segmentEnd, null, 'atencion');
          } else {
            let cursor = new Date(segmentStart);

            while (cursor < segmentEnd && faseIdx < fases.length) {
              const availableMin =
                (segmentEnd.getTime() - cursor.getTime()) / 60000;
              const takeMin = Math.min(faseRemaining, availableMin);

              if (takeMin > 0) {
                const sliceEnd = new Date(cursor.getTime() + takeMin * 60000);
                const fase = fases[faseIdx];
                const tipoF = fase.requiere_atencion ? 'atencion' : 'descanso';

                pushEvent(cursor, sliceEnd, fase, tipoF);

                cursor = sliceEnd;
                faseRemaining -= takeMin;
              }

              if (faseRemaining === 0) {
                faseIdx++;
                faseRemaining = fases[faseIdx]?.duracion_minutos ?? 0;
              } else break;
            }
          }
        });

        dayCursor.setDate(dayCursor.getDate() + 1);
        dayCursor.setHours(0, 0, 0, 0);
      }

      return eventos;
    }

    // -------------------------------
    // 3️⃣ Cita con fases en un solo día
    // -------------------------------
    if (Array.isArray(cita.fases_servicio)) {
      // Un unico bloque por cita, de la primera fase a la ultima. Los
      // descansos ya no son eventos aparte: se dibujan dentro de la tarjeta
      // a partir de fases_tramo, para que una cita larga con pausas no
      // parezca varias citas distintas.
      const totalMinutos = cita.fases_servicio.reduce(
        (acc, f) => acc + (Number(f.duracion_minutos) || 0),
        0
      );
      const finFases = new Date(startDate.getTime() + totalMinutos * 60000);

      eventos.push({
        id: `${cita.id_cita}-atencion-0`,
        groupId: cita.id_cita,
        title: cita.nombre_servicio,
        start: startDate.toISOString(),
        end: finFases.toISOString(),
        classNames: ['fase-atencion'],
        backgroundColor: 'transparent',
        borderColor: 'transparent',

        extendedProps: {
          ...commonProps,
          tipo_fase: 'atencion',
          fases_tramo: cita.fases_servicio,
          id_cita: cita.id_cita,
          id_servicio: cita.id_servicio,
          nombre_empresa: cita.nombre_empresa,
          raw_fecha_inicio: cita.fecha_cita,
          raw_fecha_fin: cita.fecha_fin,
          raw_hora_fin: cita.hora_fin,
          raw_hora_inicio: cita.hora_cita,

          // 🔥 AÑADIDO AQUÍ
          trabajadores_en_cita: cita.trabajadores_en_cita ?? null,
        },
      });

      return;
    }

    // -------------------------------
    // 4️⃣ Cita simple
    // -------------------------------
    const simpleEnd = cita.hora_fin
      ? (() => {
          const [hE, mE] = cita.hora_fin.split(':').map(Number);
          const d = new Date(cita.fecha_fin || cita.fecha_cita);
          d.setHours(hE, mE, 0, 0);
          // Reserva que cruza la medianoche sin fecha_fin informada.
          if (d.getTime() <= startDate.getTime()) d.setDate(d.getDate() + 1);
          return d;
        })()
      : new Date(startDate.getTime() + duracion_minutos * 60000);

    eventos.push({
      id: cita.id_cita,
      groupId: cita.id_cita,
      title: cita.nombre_servicio,
      start: startDate.toISOString(),
      end: simpleEnd.toISOString(),
      classNames: ['fase-atencion'],
      backgroundColor: 'transparent',
      borderColor: 'transparent',

      extendedProps: {
        ...commonProps,
        tipo_fase: 'atencion',
        id_cita: cita.id_cita,
        id_servicio: cita.id_servicio,
        nombre_empresa: cita.nombre_empresa,
        raw_fecha_inicio: cita.fecha_cita,
        raw_fecha_fin: cita.fecha_fin,
        raw_hora_fin: cita.hora_fin,
        raw_hora_inicio: cita.hora_cita,

        // 🔥 AÑADIDO AQUÍ
        trabajadores_en_cita: cita.trabajadores_en_cita ?? null,
      },
    });
  });

  return eventos;
}

/**
 * 2. Obtener horarios por día
 */
function normalizeDayName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function obtenerHorariosPorDia(
  horarios: Horario[]
): Record<number, { hora_inicio: string; hora_fin: string }[]> {
  const mapping: Record<string, number> = {
    domingo: 0,
    lunes: 1,
    martes: 2,
    miercoles: 3,
    jueves: 4,
    viernes: 5,
    sabado: 6,
  };

  const byDia: Record<number, { hora_inicio: string; hora_fin: string }[]> = {
    0: [],
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
  };

  horarios.forEach(({ dia_semana, hora_inicio, hora_fin }) => {
    const key = normalizeDayName(dia_semana);
    const idx = mapping[key];
    if (idx != null) byDia[idx].push({ hora_inicio, hora_fin });
  });

  return byDia;
}

/**
 * 3. Eventos bloqueados
 */
export function generarEventosBloqueados(
  startWeek: Date,
  horariosPorDia: Record<string, { hora_inicio: string; hora_fin: string }[]>,
  diasNoLaborables: string[]
): CalendarEvent[] {
  const eventos: CalendarEvent[] = [];
  const dias = [
    'domingo',
    'lunes',
    'martes',
    'miércoles',
    'jueves',
    'viernes',
    'sábado',
  ];
  const noLab = diasNoLaborables.map((d) => d.toLowerCase());

  dias.forEach((dia, i) => {
    if (!noLab.includes(dia)) {
      const fecha = new Date(startWeek);
      fecha.setDate(fecha.getDate() + i);

      let cursor = '00:00';

      (horariosPorDia[dia] || [])
        .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio))
        .forEach(({ hora_inicio, hora_fin }) => {
          if (cursor < hora_inicio)
            eventos.push(createBloqueoEvent(fecha, cursor, hora_inicio));
          cursor = hora_fin;
        });

      if (cursor < '23:59')
        eventos.push(createBloqueoEvent(fecha, cursor, '23:59'));
    }
  });

  return eventos;
}

function createBloqueoEvent(
  fecha: Date,
  startTime: string,
  endTime: string
): CalendarEvent {
  const s = new Date(fecha);
  const [hs, ms] = startTime.split(':').map(Number);
  s.setHours(hs, ms, 0, 0);

  const e = new Date(fecha);
  const [he, me] = endTime.split(':').map(Number);
  e.setHours(he, me, 0, 0);

  return {
    start: s.toISOString(),
    end: e.toISOString(),
    display: 'background',
    classNames: ['bloqueado-bg'],
    allDay: false,
    extendedProps: { tipo: 'bloqueado' },
  };
}

/**
 * 4. Ausencias
 */
export function buildEventosAusencias(
  ausencias: DiaAusencia[]
): CalendarEvent[] {
  return ausencias.map(({ fecha }) => {
    const start = new Date(fecha);
    const end = new Date(fecha);
    end.setDate(end.getDate() + 1);

    return {
      start: start.toISOString(),
      end: end.toISOString(),
      display: 'background',
      classNames: ['ausencia-aprobada-bg'],
      allDay: true,
      extendedProps: { tipo: 'bloqueado' },
    };
  });
}

/**
 * 5. Festivos
 */
export function buildEventosFestivos(
  fechasFestivos: string[]
): CalendarEvent[] {
  return fechasFestivos.map((fecha) => {
    const start = new Date(fecha);
    const end = new Date(fecha);
    end.setDate(end.getDate() + 1);

    return {
      start: start.toISOString(),
      end: end.toISOString(),
      display: 'background',
      classNames: ['bloqueado-bg'],
      allDay: true,
      extendedProps: { tipo: 'bloqueado' },
    };
  });
}

/**
 * 6. No laborables personalizados
 */
export function buildEventosNoLaborables(
  fechasNoLaborables: string[]
): CalendarEvent[] {
  return fechasNoLaborables.map((fecha) => ({
    start: fecha,
    end: fecha,
    display: 'background',
    classNames: ['no-laborable-bg'],
    allDay: true,
  }));
}

/**
 * 6.a Franjas por fecha -> Horario[] semanal.
 *
 * mapearCitas sigue necesitando el horario por dia de la semana para repartir
 * las citas multidia. Se deduplica porque una misma fecha puede repetir dia.
 */
export function franjasAHorarioSemanal(
  franjas: { fecha: string; hora_inicio: string; hora_fin: string }[]
): Horario[] {
  const nombres = [
    'domingo',
    'lunes',
    'martes',
    'miercoles',
    'jueves',
    'viernes',
    'sabado',
  ];

  const vistas = new Set<string>();
  const salida: Horario[] = [];

  for (const f of franjas) {
    const dia = nombres[new Date(`${f.fecha}T00:00:00`).getDay()];
    const clave = `${dia}|${f.hora_inicio}|${f.hora_fin}`;
    if (vistas.has(clave)) continue;
    vistas.add(clave);
    salida.push({
      id_horario: clave,
      dia_semana: dia,
      hora_inicio: f.hora_inicio,
      hora_fin: f.hora_fin,
    });
  }

  return salida;
}

/**
 * 6.b Sombreado fuera de jornada, fecha a fecha.
 *
 * Sustituye a businessHours de FullCalendar, que solo entiende de dias de
 * la semana y por tanto no sabe distinguir dos jueves con temporadas
 * distintas. Genera un evento de fondo por cada hueco sin jornada.
 */
export function buildEventosFueraDeJornada(
  fechas: string[],
  franjasPorFecha: Record<string, { hora_inicio: string; hora_fin: string }[]>
): CalendarEvent[] {
  const eventos: CalendarEvent[] = [];

  const bloque = (fecha: string, desde: string, hasta: string) => {
    if (desde >= hasta) return;
    eventos.push({
      start: `${fecha}T${desde}`,
      end: `${fecha}T${hasta}`,
      display: 'background',
      classNames: ['fuera-jornada-bg'],
    } as CalendarEvent);
  };

  for (const fecha of fechas) {
    const franjas = [...(franjasPorFecha[fecha] ?? [])].sort((a, b) =>
      a.hora_inicio.localeCompare(b.hora_inicio)
    );

    let cursor = '00:00:00';
    for (const f of franjas) {
      bloque(fecha, cursor, f.hora_inicio);
      if (f.hora_fin > cursor) cursor = f.hora_fin;
    }
    bloque(fecha, cursor, '23:59:59');
  }

  return eventos;
}

/**
 * 6.b.1 Descansos puntuales del trabajador.
 *
 * A diferencia de fuera-de-jornada/festivos/ausencias, esto NO es un fondo
 * pasivo: es un evento real (display 'auto') que se puede pinchar para
 * borrarlo, con su propio color para no confundirse con una cita.
 */
export function buildEventosDescansos(
  descansos: RawDescanso[]
): CalendarEvent[] {
  return descansos.map((d) => {
    const start = new Date(`${d.fecha}T${d.hora_inicio}`);
    const end = new Date(`${d.fecha}T${d.hora_fin}`);

    return {
      id: `descanso-${d.id_descanso}`,
      title: d.motivo?.trim() || 'Descanso',
      start: start.toISOString(),
      end: end.toISOString(),
      display: 'auto',
      classNames: ['descanso-evt'],
      // No se arrastra ni se redimensiona: aún no hay lógica de mover un
      // descanso, y sin esto el mismo handler que mueve citas (que lee
      // extendedProps.id_cita) se dispararía sobre un evento que no lo tiene.
      editable: false,
      startEditable: false,
      durationEditable: false,
      extendedProps: {
        tipo: 'descanso',
        id_descanso: d.id_descanso,
        id_usuario_descanso: d.id_usuario,
        motivo_descanso: d.motivo ?? null,
        raw_fecha_inicio: d.fecha,
        raw_hora_inicio: d.hora_inicio,
        raw_hora_fin: d.hora_fin,
      },
    };
  });
}

/**
 * 6.c Fechas de un rango, en formato ISO.
 */
export function fechasDelRango(desde: string, hasta: string): string[] {
  const fechas: string[] = [];
  const cur = new Date(`${desde}T00:00:00`);
  const fin = new Date(`${hasta}T00:00:00`);
  while (cur <= fin) {
    // Nada de toISOString: convierte a UTC y en España devuelve el dia
    // anterior, con lo que el sombreado se pinta en una fecha que no toca.
    const mes = String(cur.getMonth() + 1).padStart(2, '0');
    const dia = String(cur.getDate()).padStart(2, '0');
    fechas.push(`${cur.getFullYear()}-${mes}-${dia}`);
    cur.setDate(cur.getDate() + 1);
  }
  return fechas;
}

/**
 * 7. Color por ID
 */
export function colorPorId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return '#' + ((hash >> 0) & 0xffffff).toString(16).padStart(6, '0');
}